import { ZonedDateTimeFactory, zonedMin } from '@csp/csp-common-date-util';
import { Maybe, StateAssert, TimeZoneId, ZonedDateTime } from '@csp/csp-common-model';
import { compact, last, orderBy } from 'lodash';
import { RequestInitiatorType } from '../../initiation-config/model/RequestInitiatorType';
import { RequestWindow } from '../../model/RequestWindow';
import { RolloutRequestResult } from '../../model/RolloutRequestResult';
import { ScheduleReferenceDates } from '../../model/ScheduleReferenceDates';
import { ScheduleRequestRef } from '../../model/ScheduleRequestRef';
import { ScheduleRequestRefWindowInterval } from '../../model/ScheduleRequestRefWindowInterval';
import { ScheduleWindowInterval } from '../../model/ScheduleWindowInterval';
import { FixedAction } from '../../model/schedulingModels/FixedAction';
import { GenericRequest } from '../../model/schedulingModels/GenericRequest';
import { RequestWhileCriterion } from '../../model/schedulingModels/RequestWhileCriterion';
import { ScheduleVersionInfo } from '../../model/schedulingModels/ScheduleVersionInfo';
import { RequestGroupKey } from '../../request-group/model/RequestGroupKey';
import { ScheduleIntervalService } from '../../service/ScheduleIntervalService';
import { SchedulingService } from '../../service/SchedulingService';
import { RequestInitiationUtil } from '../../util/RequestInitiationUtil';
import { RequestWhileUtil } from '../../util/RequestWhileUtil';
import { RequestUserStatus, RequestUserStatuses } from './RequestUserStatus';
import { RequestVersionStatus } from './RequestVersionStatus';
import { RequestWithVersionInfo } from './RequestWithVersionInfo';
import { ScheduleRef } from './ScheduleRef';
import { ScheduleRequestInfoComparison } from './ScheduleRequestInfoComparison';

type RolloutOptions = {
  timeZoneId: TimeZoneId;
  fromDateInclusive: ZonedDateTime;
  toDateExclusive: ZonedDateTime;
  maxAmountToRollout?: number;
};

export type ScheduleRequestInfo<Request extends GenericRequest = GenericRequest> = {
  ref: ScheduleRef;
  requestUserStatus: RequestUserStatus;
  requestVersionStatus: RequestVersionStatus;
  versionInfos: ScheduleVersionInfo[];
  ongoingIntervals: ScheduleWindowInterval[];
  startDate?: ZonedDateTime;
  endDate?: ZonedDateTime;
  request: Request;
  fixedStartAction?: FixedAction;
  fixedEndAction?: FixedAction;
  hasRequestGroupsByKeys(groupKeys: RequestGroupKey[]): boolean;
  hasRequestUserStatus(statuses: RequestUserStatus[]): boolean;
  hasRequestVersionStatus(statuses: RequestVersionStatus[]): boolean;
  hasMatchingRequestWhileCriteria(criteriaToMatch: RequestWhileCriterion[]): boolean;
  isInitiatedBy(initiator: RequestInitiatorType): boolean;
  updateScheduleReferenceDates(newScheduleReferenceDates: ScheduleReferenceDates): ScheduleRequestInfo<Request>;
  compare(
    userRefDates: ScheduleReferenceDates,
    timeZoneId: TimeZoneId,
    maxDate?: ZonedDateTime,
  ): Promise<ScheduleRequestInfoComparison>;
  rolloutBetweenDates(options: RolloutOptions): Promise<RolloutRequestResult>;
};

const isPassed = (requestWindow: RequestWindow, maxDate: ZonedDateTime): boolean =>
  !!requestWindow.windowEnd && requestWindow.windowEnd.unixTimeMillis < maxDate.unixTimeMillis;

const isInterrupted = (requestWindow: RequestWindow): boolean =>
  requestWindow.isInterrupted ||
  (!!requestWindow.validity?.validTo &&
    !!requestWindow.windowEnd &&
    requestWindow.validity.validTo.unixTimeMillis < requestWindow.windowEnd.unixTimeMillis);

const isMissed = (requestWindow: RequestWindow, maxDate: ZonedDateTime): boolean =>
  isPassed(requestWindow, maxDate) && !isInterrupted(requestWindow);

const from = <Request extends GenericRequest>(
  request: RequestWithVersionInfo<Request>,
  scheduleReferenceDates: ScheduleReferenceDates,
): ScheduleRequestInfo<Request> => {
  const { firstStartToLastEnd, intervals } = SchedulingService.getUserOngoingIntervals(request, scheduleReferenceDates);
  const startDate = firstStartToLastEnd?.windowStart;
  const endDate = firstStartToLastEnd?.windowEnd;
  const requestUserStatus = RequestUserStatuses.from(request.versionStatus, startDate, endDate);

  const hasRequestUserStatus = (statuses: RequestUserStatus[]): boolean =>
    statuses.length === 0 || statuses.includes(requestUserStatus);

  const hasRequestGroupsByKeys = (groupKeys: RequestGroupKey[]): boolean =>
    !!(groupKeys.length === 0 || request.groups?.includesOneOf(groupKeys));

  const hasRequestVersionStatus = (statuses: RequestVersionStatus[]): boolean =>
    statuses.length === 0 || statuses.includes(request.versionStatus);

  const isInitiatedBy = (initiatedBy: RequestInitiatorType): boolean =>
    RequestInitiationUtil.isInitiatedBy(request.initiationConfig, initiatedBy);

  const hasMatchingRequestWhileCriteria = (criteriaToMatch: RequestWhileCriterion[]): boolean =>
    !!RequestWhileUtil.filterRequestsWithMatchingCriteria(criteriaToMatch, [request]).length;

  const updateScheduleReferenceDates = (
    newScheduleReferenceDates: ScheduleReferenceDates,
  ): ScheduleRequestInfo<Request> => from(request, newScheduleReferenceDates);

  const compare = async (
    newScheduleReferenceDates: ScheduleReferenceDates,
    timeZoneId: TimeZoneId,
    maxDate = ZonedDateTimeFactory.now(timeZoneId),
  ): Promise<ScheduleRequestInfoComparison> => {
    const newRequestInfo = updateScheduleReferenceDates(newScheduleReferenceDates);

    const differences = ScheduleIntervalService.symmetricDifference(intervals, newRequestInfo.ongoingIntervals);
    const differencesWithRequestRef: ScheduleRequestRefWindowInterval[] = differences
      .filter(difference => difference.windowStart.unixTimeMillis < maxDate.unixTimeMillis)
      .map(difference => ({
        ...difference,
        requestRef: ScheduleRequestRef.fromGenericRequest(request),
      }));

    const hasChanged = !!differencesWithRequestRef.length;
    const earliestDiffStartDate = differencesWithRequestRef[0]?.windowStart;
    const latestDiffEndDate = last(differencesWithRequestRef)?.windowEnd;
    const comparison =
      hasChanged && isInitiatedBy(RequestInitiatorType.SCHEDULE)
        ? await SchedulingService.compareRequestRolloutsBetweenDates({
            request,
            timeZoneId,
            oldReferenceDates: scheduleReferenceDates,
            newReferenceDates: newScheduleReferenceDates,
            fromDateInclusive: zonedMin(compact([earliestDiffStartDate, maxDate])) ?? maxDate,
            toDateExclusive: zonedMin(compact([latestDiffEndDate, maxDate])) ?? maxDate,
          })
        : undefined;

    const missedWindows =
      comparison?.addedRequestWindows.filter(requestWindow => isMissed(requestWindow, maxDate)) ?? [];
    const affectedStartDate = missedWindows[0]?.windowStart;
    const affectedEndDate = last(missedWindows)?.windowEnd;

    return {
      hasChanged,
      currentEndDate: endDate,
      currentStartDate: startDate,
      currentStatus: requestUserStatus,
      newEndDate: newRequestInfo.endDate,
      newStartDate: newRequestInfo.startDate,
      newStatus: newRequestInfo.requestUserStatus,
      affectedInterval: affectedStartDate ? { windowStart: affectedStartDate, windowEnd: affectedEndDate } : undefined,
      missedRequestWindows: missedWindows,
      numberOfMissedWindows: missedWindows.reduce(
        (missedCount, currentWindow) => missedCount + currentWindow.frequency,
        0,
      ),
    };
  };

  const rolloutBetweenDates = async ({
    fromDateInclusive,
    timeZoneId,
    toDateExclusive,
    maxAmountToRollout,
  }: RolloutOptions): Promise<RolloutRequestResult> =>
    await SchedulingService.rolloutRequestBetweenDates({
      request,
      scheduleReferenceDates,
      fromDateInclusive,
      toDateExclusive,
      versionWindows: request.versions,
      timeZoneId,
      maxAmountToRollout,
    });

  return {
    ref: ScheduleRef.fromRequest(request),
    startDate,
    endDate,
    requestUserStatus,
    requestVersionStatus: request.versionStatus,
    request,
    fixedStartAction: request.startAction?.fixedAction,
    fixedEndAction: request.endAction?.fixedAction,
    versionInfos: request.versions,
    ongoingIntervals: intervals,
    hasRequestUserStatus,
    hasRequestGroupsByKeys,
    hasRequestVersionStatus,
    hasMatchingRequestWhileCriteria,
    isInitiatedBy,
    updateScheduleReferenceDates,
    compare,
    rolloutBetweenDates,
  };
};

const evalRequestUserStatus = (requestInfos: ScheduleRequestInfo[]): RequestUserStatus => {
  const statuses = requestInfos.map(requestInfo => requestInfo.requestUserStatus);
  if (statuses.includes(RequestUserStatus.ONGOING)) {
    return RequestUserStatus.ONGOING;
  } else if (statuses.includes(RequestUserStatus.DONE)) {
    return RequestUserStatus.DONE;
  } else if (statuses.includes(RequestUserStatus.EXPIRED)) {
    return RequestUserStatus.EXPIRED;
  } else if (statuses.includes(RequestUserStatus.UPCOMING)) {
    return RequestUserStatus.UPCOMING;
  } else {
    return RequestUserStatus.NOT_APPLICABLE;
  }
};

const evalRequestVersionStatus = (requestInfos: ScheduleRequestInfo[]): RequestVersionStatus => {
  const evalRequestVersionInternal = (requestInfos: ScheduleRequestInfo[]): RequestVersionStatus => {
    const statuses = requestInfos.map(requestInfo => requestInfo.requestVersionStatus);
    if (statuses.includes(RequestVersionStatus.ACTIVE)) {
      return RequestVersionStatus.ACTIVE;
    } else if (statuses.includes(RequestVersionStatus.EXPIRED)) {
      return RequestVersionStatus.EXPIRED;
    } else if (statuses.includes(RequestVersionStatus.FUTURE)) {
      return RequestVersionStatus.FUTURE;
    } else {
      // If we ended up here it is either because there we no request
      // infos to begin, in which case the status doesn't matter.
      return RequestVersionStatus.ACTIVE;
    }
  };

  // We are first and foremost interested in the requests that are
  // applicable to the patient. This solves the following scenario:
  // 1. S1 -> V1 -> R1 is ACTIVE and ONGOING
  // 2. S1 -> V2 -> R2 is activated instead after a protocol amendment.
  //    R2 is NOT_APPLICABLE to the patient.
  // 3. When merging R1 and R2 we want the request info to have
  //    verion status EXPIRED because all requests in the new version (V2) are
  //    NOT_APPLICABLE
  //
  // If all requests are not applicable then we use those to evaluate the version status.
  const applicableRequestInfos = requestInfos.filter(
    requestInfo => requestInfo.requestUserStatus !== RequestUserStatus.NOT_APPLICABLE,
  );
  return applicableRequestInfos.length
    ? evalRequestVersionInternal(applicableRequestInfos)
    : evalRequestVersionInternal(requestInfos);
};

const merge = <Request extends GenericRequest>(
  allRequestInfos: ScheduleRequestInfo<Request>[],
): Maybe<ScheduleRequestInfo<Request>> => {
  const internalRequestUserStatus = evalRequestUserStatus(allRequestInfos);
  const requestVersionStatus = evalRequestVersionStatus(allRequestInfos);

  // Special case: If we are "between" 2 request:
  // * one that has ended,
  // * one that is upcoming,
  // * nothing ongoing currently
  // Then we should show it as ongoing (even if it technically isn't currently).
  const publicRequestUserStatus =
    internalRequestUserStatus === RequestUserStatus.DONE &&
    allRequestInfos.some(requestInfo => requestInfo.requestUserStatus === RequestUserStatus.UPCOMING)
      ? RequestUserStatus.ONGOING
      : internalRequestUserStatus;

  // When merging request infos we must look at all requests when evaluating the start/end date
  // and the reference (to fetch metrics and responses), but only the latest (non-future) version when evaluating
  // other configurations.
  //
  // If for instance a schedule had a on-demand request in an
  // expired version of the schedule but removed in the current, this API should return false for isInitiatedBy('HCP').
  //
  // We do this by looking only on the request infos that have the same status as the group as a whole, i.e.:
  // * If the group is ongoing, it is hcp initiated if any of the ongoing request infos are hcp initiated.
  const relevantRequestInfos = allRequestInfos.filter(
    requestInfo =>
      requestInfo.requestUserStatus === internalRequestUserStatus &&
      requestInfo.requestVersionStatus === requestVersionStatus,
  );
  const firstRequestInfo = relevantRequestInfos[0];

  if (firstRequestInfo) {
    const requestInfosWithStartDate = allRequestInfos.filter(req => !!req.startDate);
    const requestInfoThatStartedFirst = orderBy(
      requestInfosWithStartDate,
      requestInfo => requestInfo.startDate?.unixTimeMillis,
      'asc',
    )[0];
    const requestWithLatestEndDate = orderBy(
      requestInfosWithStartDate,
      requestInfo => requestInfo.endDate?.unixTimeMillis,
      'desc',
    )[0];
    const requestWithLatestEndAction =
      requestInfosWithStartDate.find(request => !request.fixedEndAction) ?? requestWithLatestEndDate;

    const startDate = requestInfoThatStartedFirst?.startDate;
    const endDate = requestWithLatestEndDate?.endDate;

    const hasRequestUserStatus = (statuses: RequestUserStatus[]): boolean =>
      statuses.length === 0 || statuses.includes(internalRequestUserStatus);

    const hasRequestGroupsByKeys = (groupKeys: RequestGroupKey[]): boolean =>
      groupKeys.length === 0 || relevantRequestInfos.some(req => req.request.groups?.includesOneOf(groupKeys));

    const hasRequestVersionStatus = (statuses: RequestVersionStatus[]): boolean =>
      statuses.length === 0 || statuses.includes(requestVersionStatus);

    const isInitiatedBy = (initiator: RequestInitiatorType): boolean =>
      relevantRequestInfos.some(requestInfo => requestInfo.isInitiatedBy(initiator));

    const hasMatchingRequestWhileCriteria = (matchingCriterionTemplates: RequestWhileCriterion[]): boolean =>
      !!RequestWhileUtil.filterRequestsWithMatchingCriteria(
        matchingCriterionTemplates,
        relevantRequestInfos.map(requestInfo => requestInfo.request),
      ).length;

    const scheduleRef = ScheduleRef.fromSchedule(
      firstRequestInfo.request.scheduleCode,
      allRequestInfos.map(reqInfo => reqInfo.request),
    );

    const onDemandInitiatedRequest = relevantRequestInfos.find(
      request =>
        request.isInitiatedBy(RequestInitiatorType.HCP) || request.isInitiatedBy(RequestInitiatorType.RPM_EVENT),
    );

    const updateScheduleReferenceDates = (
      newScheduleReferenceDates: ScheduleReferenceDates,
    ): ScheduleRequestInfo<Request> => {
      const newRequestInfos = allRequestInfos.map(currRequestInfo =>
        currRequestInfo.updateScheduleReferenceDates(newScheduleReferenceDates),
      );
      const newMergedRequestInfo = merge(newRequestInfos);
      StateAssert.isDefined(newMergedRequestInfo, 'Unexpected undefined request info');
      return newMergedRequestInfo;
    };

    const compare = async (
      newScheduleReferenceDates: ScheduleReferenceDates,
      timeZoneId: TimeZoneId,
      maxDate = ZonedDateTimeFactory.now(timeZoneId),
    ): Promise<ScheduleRequestInfoComparison> => {
      const newMergedRequestInfo = updateScheduleReferenceDates(newScheduleReferenceDates);
      const comparisons = await Promise.all(
        allRequestInfos.map(currRequestInfo => currRequestInfo.compare(newScheduleReferenceDates, timeZoneId, maxDate)),
      );
      const comparisonWithOldestAffectedInterval = orderBy(
        comparisons,
        comparison => comparison.affectedInterval?.windowStart,
        'asc',
      )[0];
      return {
        hasChanged: comparisons.some(comparison => comparison.hasChanged),
        currentEndDate: endDate,
        currentStartDate: startDate,
        currentStatus: internalRequestUserStatus,
        newEndDate: newMergedRequestInfo.endDate,
        newStartDate: newMergedRequestInfo.startDate,
        newStatus: newMergedRequestInfo.requestUserStatus,
        affectedInterval: comparisonWithOldestAffectedInterval?.affectedInterval,
        missedRequestWindows: orderBy(
          comparisons.flatMap(comparison => comparison.missedRequestWindows),
          window => window.windowStart.unixTimeMillis,
          'asc',
        ),
        numberOfMissedWindows: comparisons.reduce(
          (numMissed, comparison) => numMissed + comparison.numberOfMissedWindows,
          0,
        ),
      };
    };

    const rolloutBetweenDates = async (rolloutOptions: RolloutOptions): Promise<RolloutRequestResult> => {
      const allRepeatingResults = await Promise.all(
        allRequestInfos
          .filter(requestInfo => requestInfo.isInitiatedBy(RequestInitiatorType.SCHEDULE))
          .map(requestInfo => requestInfo.rolloutBetweenDates(rolloutOptions)),
      );
      return RolloutRequestResult.fromResults(allRepeatingResults);
    };

    const versionInfos = firstRequestInfo.versionInfos;
    const ongoingIntervals = ScheduleIntervalService.union(
      allRequestInfos.flatMap(({ ongoingIntervals }) => ongoingIntervals),
    );

    return {
      requestVersionStatus,
      requestUserStatus: publicRequestUserStatus,
      fixedStartAction: requestInfoThatStartedFirst?.fixedStartAction,
      fixedEndAction: requestWithLatestEndAction?.fixedEndAction,
      startDate: requestInfoThatStartedFirst?.startDate,
      endDate: requestWithLatestEndDate?.endDate,
      versionInfos,
      ongoingIntervals,
      // The embedded request is a mix of all the requests this object is made of.
      request: {
        ...firstRequestInfo.request,
        // Using the on-demand request as reference maintains the contract for when this is used to send messages.
        initiationConfig:
          onDemandInitiatedRequest?.request.initiationConfig ?? firstRequestInfo.request.initiationConfig,
        versionCode: onDemandInitiatedRequest?.request.versionCode ?? firstRequestInfo.request.versionCode,
        requestCode: onDemandInitiatedRequest?.request.requestCode ?? firstRequestInfo.request.requestCode,
        startAction: requestInfoThatStartedFirst?.request.startAction,
        endAction: requestWithLatestEndAction?.request.endAction,
      },
      ref: scheduleRef,
      hasRequestUserStatus,
      hasRequestGroupsByKeys,
      hasRequestVersionStatus,
      hasMatchingRequestWhileCriteria,
      isInitiatedBy,
      updateScheduleReferenceDates,
      compare,
      rolloutBetweenDates,
    };
  } else {
    return undefined;
  }
};

export const ScheduleRequestInfo = {
  from,
  merge,
};
